import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { LanguageProvider } from "./lib/i18n";
import { Toaster } from "./components/ui/sonner";
import { tintStatusBar, isNative } from "./lib/native";

import Landing from "./pages/Landing";
import Rates from "./pages/Rates";
import Support from "./pages/Support";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SmsPolicy from "./pages/SmsPolicy";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CustomerDashboard from "./pages/CustomerDashboard";
import CardDetail from "./pages/CardDetail";
import AdminPanel from "./pages/AdminPanel";
import SetupChecklist from "./pages/admin/SetupChecklist";
import TwilioTfvGuide from "./pages/admin/TwilioTfvGuide";
import Twilio10dlcGuide from "./pages/admin/Twilio10dlcGuide";
import StripeToWiseGuide from "./pages/admin/StripeToWiseGuide";
import StripeIssuingGuide from "./pages/admin/StripeIssuingGuide";
import StripeIssuingApplication from "./pages/admin/StripeIssuingApplication";
import StripeIssuingDemo from "./pages/admin/StripeIssuingDemo";
import WalletCredentialsGuide from "./pages/admin/WalletCredentialsGuide";
import OfacHitsAdmin from "./pages/admin/OfacHitsAdmin";
import IssuingReconciliationAdmin from "./pages/admin/IssuingReconciliationAdmin";
import SendInternational from "./pages/SendInternational";
import ManagerPanel from "./pages/ManagerPanel";
import Pay from "./pages/Pay";
import CashRequestStatus from "./pages/CashRequestStatus";
import DirectDeposits from "./pages/DirectDeposits";
import DepositPortal from "./pages/DepositPortal";
import ReferralLanding from "./pages/ReferralLanding";
import Install from "./pages/Install";
import PayoutOnboarding from "./pages/PayoutOnboarding";
import VietQRSetup from "./pages/VietQRSetup";
import IssuingTermsAccept from "./pages/IssuingTermsAccept";
import ReceiveHub from "./pages/ReceiveHub";
import StripeIssuingPreflight from "./pages/admin/StripeIssuingPreflight";
import BusinessIdentity from "./pages/admin/BusinessIdentity";
import CardholderAgreement from "./pages/admin/CardholderAgreement";
import LLCFormationGuide from "./pages/admin/LLCFormationGuide";
import I18nCoverage from "./pages/admin/I18nCoverage";
import NotificationPreferences from "./pages/admin/NotificationPreferences";
import AgentsAdmin from "./pages/admin/AgentsAdmin";
import AgentApplicationsAdmin from "./pages/admin/AgentApplicationsAdmin";
import AgentPayoutsAdmin from "./pages/admin/AgentPayoutsAdmin";
import AgentPortal from "./pages/AgentPortal";
import AgentApply from "./pages/AgentApply";
import SetPassword from "./pages/SetPassword";
import Transactions from "./pages/Transactions";
import ImpersonationBanner from "./components/ImpersonationBanner";
import IosInstallPrompt from "./components/IosInstallPrompt";
import InstallScrollNudge from "./components/InstallScrollNudge";
import SmartAppBanner from "./components/SmartAppBanner";
import DeepLinkHandler from "./components/DeepLinkHandler";
import StandaloneBootTracker from "./components/StandaloneBootTracker";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#4A5C50]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  React.useEffect(() => { tintStatusBar(); }, []);
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <DeepLinkHandler />
          <StandaloneBootTracker />
          <ImpersonationBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/rates" element={<Rates />} />
            <Route path="/support" element={<Support />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/sms-policy" element={<SmsPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Protected roles={["customer", "admin", "manager"]}><CustomerDashboard /></Protected>} />
            <Route path="/cards/:cardId" element={<Protected><CardDetail /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin"]}><AdminPanel /></Protected>} />
            <Route path="/admin/setup" element={<Protected roles={["admin"]}><SetupChecklist /></Protected>} />
            <Route path="/admin/twilio-tfv-guide" element={<Protected roles={["admin"]}><TwilioTfvGuide /></Protected>} />
            <Route path="/admin/twilio-10dlc-guide" element={<Protected roles={["admin"]}><Twilio10dlcGuide /></Protected>} />
            <Route path="/admin/stripe-to-wise-guide" element={<Protected roles={["admin"]}><StripeToWiseGuide /></Protected>} />
            <Route path="/admin/stripe-issuing-guide" element={<Protected roles={["admin"]}><StripeIssuingGuide /></Protected>} />
            <Route path="/admin/stripe-issuing-application" element={<Protected roles={["admin"]}><StripeIssuingApplication /></Protected>} />
            <Route path="/admin/stripe-issuing-demo" element={<Protected roles={["admin"]}><StripeIssuingDemo /></Protected>} />
            <Route path="/admin/wallet-credentials-guide" element={<Protected roles={["admin"]}><WalletCredentialsGuide /></Protected>} />
            <Route path="/admin/ofac-hits" element={<Protected roles={["admin"]}><OfacHitsAdmin /></Protected>} />
            <Route path="/admin/issuing-reconciliation" element={<Protected roles={["admin"]}><IssuingReconciliationAdmin /></Protected>} />
            <Route path="/send-international" element={<Protected><SendInternational /></Protected>} />
            <Route path="/manager" element={<Protected roles={["admin", "manager"]}><ManagerPanel /></Protected>} />
            <Route path="/pay" element={<Pay />} />
            <Route path="/cash-request/:cardId" element={<CashRequestStatus />} />
            <Route path="/direct-deposits" element={<Protected roles={["customer", "admin", "manager"]}><DirectDeposits /></Protected>} />
            <Route path="/deposit/:token" element={<DepositPortal />} />
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/install" element={<Install />} />
            <Route path="/payout-onboarding/:token" element={<PayoutOnboarding />} />
            <Route path="/vietqr-setup/:token" element={<VietQRSetup />} />
            <Route path="/issuing/accept-terms/:token" element={<IssuingTermsAccept />} />
            <Route path="/receive/:token" element={<ReceiveHub />} />
            <Route path="/admin/stripe-issuing-preflight" element={<Protected roles={["admin","manager"]}><StripeIssuingPreflight /></Protected>} />
            <Route path="/admin/business-identity" element={<Protected roles={["admin","manager"]}><BusinessIdentity /></Protected>} />
            <Route path="/admin/cardholder-agreement" element={<Protected roles={["admin","manager"]}><CardholderAgreement /></Protected>} />
            <Route path="/admin/llc-formation-guide" element={<Protected roles={["admin","manager"]}><LLCFormationGuide /></Protected>} />
            <Route path="/admin/i18n-coverage" element={<Protected roles={["admin","manager"]}><I18nCoverage /></Protected>} />
            <Route path="/admin/notification-preferences" element={<Protected roles={["admin","manager"]}><NotificationPreferences /></Protected>} />
            <Route path="/admin/agents" element={<Protected roles={["admin"]}><AgentsAdmin /></Protected>} />
            <Route path="/admin/agent-applications" element={<Protected roles={["admin"]}><AgentApplicationsAdmin /></Protected>} />
            <Route path="/admin/agent-payouts" element={<Protected roles={["admin"]}><AgentPayoutsAdmin /></Protected>} />
            <Route path="/agent" element={<Protected roles={["agent"]}><AgentPortal /></Protected>} />
            <Route path="/agent/apply" element={<AgentApply />} />
            <Route path="/set-password/:token" element={<SetPassword />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {!isNative() && <SmartAppBanner />}
          {!isNative() && <IosInstallPrompt />}
          {!isNative() && <InstallScrollNudge />}
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </LanguageProvider>
  );
}
